<template>
  <TbLoading
    v-if="state === PageState.loading"
    class="flex items-center justify-center w-full h-full"
  />
  <div v-else-if="state === PageState.loaded">
    <p
      v-if="!paymentPlans?.plans?.length"
      class="mb-4 text-lg font-bold"
    >
      This application was not qualified for any offers.
    </p>

    <div v-else>
      <h1 class="mb-4 text-xl font-bold">
        Plans Offered
      </h1>

      <div
        v-for="plan in paymentPlans?.plans"
        :key="plan.installments"
        class="flex flex-col mb-4"
      >
        <span class="mb-1 font-semibold">
          {{ plan.installments }} Monthly Payments
        </span>
        <span class="mb-1">
          {{ plan.currencyCode }}{{ currencyInCents(plan.firstPaymentInCents) }}
          down with the rest split over {{ plan.installments - 1 }} monthly payments{{ plan.apr && ` at ${plan.apr}% APR` }}.
        </span>
      </div>
    </div>
    <button
      class="mt-4 btn btn-secondary btn-block"
      @click="cancel"
    >
      Close
    </button>
  </div>

  <div
    v-else
    class="flex flex-col items-center w-full text-center"
  >
    <h3 class="text-lg font-semibold mb-2 text-red-600">
      Unable to load payment plans
    </h3>
    <p class="text-sm text-gray-600 mb-4">
      {{ errorMessage }}
    </p>
    <button
      class="btn btn-secondary btn-sm"
      @click="retry"
    >
      Try Again
    </button>
  </div>
</template>

<script lang="ts" setup>
import {
  onMounted, ref, Ref,
} from 'vue';
import { useRoute } from 'vue-router';
import { currencyInCents } from '@/filters/currency';
import { TbLoading } from '@/components/tasty_bistro';
import { PageState } from '@/types';
import { getErrorMessage } from '@/lib/http';
import { getPaymentPlans, Plans } from '../api/get_payment_plans';

const route = useRoute();
const projectId = route.params.projectId as string;
const emit = defineEmits([
  'cancel',
]);

const props = defineProps<{ applicationId: string }>();
const paymentPlans = ref<Plans>();
const state: Ref<PageState> = ref(PageState.loading);
const errorMessage = ref<string>('');

function cancel() {
  emit('cancel');
}

async function retry() {
  await loadPaymentPlans();
}

async function loadPaymentPlans() {
  try {
    state.value = PageState.loading;
    errorMessage.value = '';
    const data = await getPaymentPlans({ id: props.applicationId, projectId });
    data.plans.sort((a, b) => a.installments - b.installments);
    paymentPlans.value = data;
    state.value = PageState.loaded;
  } catch (err) {
    errorMessage.value = getErrorMessage(err);
    state.value = PageState.error;
  }
}

onMounted(async () => {
  await loadPaymentPlans();
});
</script>
